/**
 * What the VR interface's menus list (vrInterface.ts draws them): pure functions from the same
 * state the page UI reads, so ticking a layer or a folder in VR changes the scene exactly as the
 * page's menus do, and both stay in sync.
 *
 * - The wrist menu: one entry per menu the scene offers (Waypoints, the visitor directory, Layers,
 *   Measurements, Scenes), then Sound and Exit VR.
 * - Waypoints: the tour stops in the waypoints menu, then the extra waypoints shown in it.
 * - The directory: its folder tree (tick boxes, open folders), hotspots and views; a search line
 *   filters it like the page's (folder names, hotspot and view titles; case and accents ignored).
 * - Layers: the regions in the Layers menu, by heading, with the "Show all" entry.
 * - Measurements: show / hide the saved measurements, then the measurement sets.
 * - Scenes: the scene menu's portals and links, by folder.
 *
 * Row ids are the actions vrInterface runs ('stop:3', 'view:<id>', 'folder:<id>', …).
 */
import type { VrCheck, VrListRow } from './vrPanelKit';
import type { DirectoryFolder, DirectoryView, VisitorDirectoryState } from '../visitorDirectory';
import type { LayersMenuSource } from '../regionsRuntime';
import type { MeasurementSetsState } from '../../utils/measurementSets';
import { OTHER_MEASUREMENTS_SET_ID } from '../../utils/measurementSets';
import type { PortalData, SceneMenuLink } from '../../types';

/** The words of the VR menus: the scene's label overrides, else English (built by the host). */
export interface VrLabels {
    menu: string;
    waypoints: string;
    next: string;
    previous: string;
    scenes: string;
    mute: string;
    unmute: string;
    exitVr: string;
    close: string;
    measurements: string;
    measurementSets: string;
    otherMeasurements: string;
    allMeasurements: string;
    showMeasurements: string;
    views: string;
    search: string;
    noMatch: string;
    /** A folder's count ("12 hotspots", "3 items"). */
    count: (hotspots: number, views: number) => string;
    savedLinks: string;
    savedLinksTitle: string;
    saveLink: string;
    linkSaved: string;
    play: string;
    pause: string;
    photos: string;
    video: string;
    pdf: string;
    modelOutsideVr: string;
    webContentOutsideVr: string;
}

export type VrMenuId = 'waypoints' | 'directory' | 'layers' | 'measurements' | 'scenes' | 'links';

export interface VrWristEntry {
    id: VrMenuId | 'sound' | 'exit';
    label: string;
    /** The panel it opens is open now. */
    open?: boolean;
    detail?: string;
}

/** What the scene offers, for the wrist menu. */
export interface VrMenuAvailability {
    waypoints: boolean;
    /** The directory's title, or null when it is off or lists nothing. */
    directory: string | null;
    /** The Layers menu's title, or null without one. */
    layers: string | null;
    measurements: boolean;
    scenes: boolean;
    /** Null: the scene has no sound. */
    muted: boolean | null;
    savedLinks: number;
}

export function vrWristEntries(available: VrMenuAvailability, labels: VrLabels, open: VrMenuId | null): VrWristEntry[] {
    const entries: VrWristEntry[] = [];
    const add = (id: VrMenuId, label: string) => entries.push({ id, label, open: open === id });
    if (available.waypoints) add('waypoints', labels.waypoints);
    if (available.directory) add('directory', available.directory);
    if (available.layers) add('layers', available.layers);
    if (available.measurements) add('measurements', labels.measurements);
    if (available.scenes) add('scenes', labels.scenes);
    if (available.savedLinks > 0) entries.push({ id: 'links', label: labels.savedLinks, detail: String(available.savedLinks), open: open === 'links' });
    if (available.muted !== null) entries.push({ id: 'sound', label: available.muted ? labels.unmute : labels.mute });
    entries.push({ id: 'exit', label: labels.exitVr });
    return entries;
}

/** The wrist menu's rows. */
export function wristRows(entries: readonly VrWristEntry[]): VrListRow[] {
    return entries.map((e) => ({ id: `wrist:${e.id}`, label: e.label, detail: e.detail, active: e.open }));
}

// ── Waypoints ─────────────────────────────────────────────────────────────────────────────────

export interface VrStopSource {
    name?: string;
    /** Left out of the waypoints menu. */
    hideInMenu?: boolean;
}

/** The waypoints menu: tour stops (not hidden from it), then the extra waypoints shown in it. */
export function waypointRows(stops: readonly VrStopSource[], extras: ReadonlyArray<{ id: string; name: string }>, current: number): VrListRow[] {
    const rows: VrListRow[] = [];
    stops.forEach((stop, index) => {
        if (stop.hideInMenu === true) return;
        const name = stop.name?.trim() || `Waypoint ${index + 1}`;
        rows.push({ id: `stop:${index}`, label: `${index + 1}. ${name}`, active: index === current });
    });
    if (extras.length) {
        if (rows.length) rows.push({ id: 'divider:extras', label: '', heading: true });
        for (const extra of extras) rows.push({ id: `view:${extra.id}`, label: extra.name });
    }
    return rows;
}

// ── The visitor directory ─────────────────────────────────────────────────────────────────────

/** Case- and accent-insensitive text for the search, like the page directory's. */
// Combining accents as escapes: raw characters in a regex break the bundle on Latin-1 pages.
export const foldSearch = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const checkOf = (state: 'on' | 'off' | 'mixed' | null): VrCheck | undefined => (state === null ? undefined : state);

/**
 * The directory's rows: hotspots not in a folder, the folder tree (a folder opens when selected;
 * its tick box shows or hides its hotspots and those below it), then Views not in a folder. With a
 * `query`, a folder whose name matches shows everything in it, any other match shows inside its
 * folders (opened), and the rest is left out.
 */
export function directoryRows(
    state: Pick<VisitorDirectoryState, 'getUnfiled' | 'getTopFolders' | 'getViews' | 'countsOf' | 'folderCheckState'>,
    options: { expanded: ReadonlySet<string>; query?: string; isHotspotAvailable?: (id: string) => boolean },
    labels: Pick<VrLabels, 'views' | 'count'>,
): VrListRow[] {
    const q = foldSearch((options.query ?? '').trim());
    const match = (text: string) => !q || foldSearch(text).includes(q);
    const available = options.isHotspotAvailable ?? (() => true);
    const rows: VrListRow[] = [];
    const hotspotRow = (h: { id: string; title: string; color?: string }, depth: number): VrListRow => ({
        id: `hotspot:${h.id}`, label: h.title, depth, swatch: h.color ?? '#9aa0a6', disabled: !available(h.id),
    });
    const viewRow = (v: DirectoryView, depth: number): VrListRow => ({ id: `dview:${v.id}`, label: v.name, depth, sub: undefined, swatch: undefined, detail: '↗' });

    for (const h of state.getUnfiled()) if (match(h.title)) rows.push(hotspotRow(h, 0));

    /** A folder's rows, or none when nothing in it matches. `all`: an ancestor's name matched. */
    const folderRows = (folder: DirectoryFolder, depth: number, all: boolean): VrListRow[] => {
        const nameMatches = all || (!!q && match(folder.name));
        const inner: VrListRow[] = [];
        for (const child of folder.children) inner.push(...folderRows(child, depth + 1, nameMatches));
        for (const v of folder.views) if (nameMatches || match(v.name)) inner.push(viewRow(v, depth + 1));
        for (const h of folder.hotspots) if (nameMatches || match(h.title)) inner.push(hotspotRow(h, depth + 1));
        const counts = state.countsOf(folder.id);
        if (!counts.hotspots && !counts.views) return [];
        if (q && !nameMatches && inner.length === 0) return [];
        // Searching opens the folders around the matches; otherwise the visitor's choice.
        const open = q ? true : options.expanded.has(folder.id);
        const row: VrListRow = {
            id: `folder:${folder.id}`,
            label: folder.name,
            depth,
            expand: open ? 'open' : 'closed',
            detail: labels.count(counts.hotspots, counts.views),
            check: checkOf(state.folderCheckState(folder.id)),
            checkId: `check:${folder.id}`,
        };
        return open ? [row, ...inner] : [row];
    };
    for (const folder of state.getTopFolders(true)) rows.push(...folderRows(folder, 0, false));

    const views = state.getViews().filter((v) => match(v.name));
    if (views.length) {
        rows.push({ id: 'heading:views', label: labels.views, heading: true });
        for (const v of views) rows.push(viewRow(v, 0));
    }
    return rows;
}

// ── Layers ────────────────────────────────────────────────────────────────────────────────────

/** The Layers menu's rows: "Show all" (when offered), layers without a heading, then each heading's. */
export function layerRows(source: Pick<LayersMenuSource, 'panel' | 'getGroups' | 'isVisible' | 'allVisible'>): VrListRow[] {
    const groups = source.getGroups();
    const single = source.panel.selectionMode === 'single';
    const all = source.allVisible();
    const rows: VrListRow[] = [];
    if (source.panel.showAllOption && groups.length > 1) {
        rows.push({ id: 'layer-all', label: source.panel.allLabel, check: all ? 'on' : 'off', radio: single });
    }
    const row = (g: (typeof groups)[number]): VrListRow => ({
        id: `layer:${g.id}`,
        label: g.name,
        sub: g.description,
        swatch: g.color,
        // One at a time: "Show all" stands in for every layer, so the rows read as one choice.
        check: source.isVisible(g.id) && !(single && all && groups.length > 1) ? 'on' : 'off',
        radio: single,
    });
    const headings: string[] = [];
    for (const g of groups) if (g.section && !headings.includes(g.section)) headings.push(g.section);
    for (const g of groups) if (!g.section) rows.push(row(g));
    for (const heading of headings) {
        rows.push({ id: `heading:${heading}`, label: heading, heading: true });
        for (const g of groups) if (g.section === heading) rows.push(row(g));
    }
    return rows;
}

// ── Measurements ──────────────────────────────────────────────────────────────────────────────

/**
 * The Measurements panel: "Show measurements" (the saved measurements in VR), then, when the
 * scene has sets, a tick box per set that holds measurements and "Show all measurements".
 */
export function measurementRows(sets: Pick<MeasurementSetsState, 'menuEntries' | 'allVisible' | 'noneVisible'> | null, shown: boolean, labels: Pick<VrLabels, 'showMeasurements' | 'measurementSets' | 'otherMeasurements' | 'allMeasurements'>): VrListRow[] {
    const rows: VrListRow[] = [{ id: 'measure:toggle', label: labels.showMeasurements, check: shown ? 'on' : 'off' }];
    const entries = sets?.menuEntries() ?? [];
    if (!entries.length || !sets) return rows;
    rows.push({ id: 'heading:sets', label: labels.measurementSets, heading: true });
    if (entries.length > 1) {
        rows.push({ id: 'mset-all', label: labels.allMeasurements, check: sets.allVisible() ? 'on' : sets.noneVisible() ? 'off' : 'mixed', disabled: !shown });
    }
    for (const e of entries) {
        rows.push({
            id: `mset:${e.id}`,
            label: e.id === OTHER_MEASUREMENTS_SET_ID ? labels.otherMeasurements : e.name,
            detail: String(e.count),
            check: e.visible ? 'on' : 'off',
            disabled: !shown,
        });
    }
    return rows;
}

// ── Scenes (the scene menu) ───────────────────────────────────────────────────────────────────

export interface VrSceneMenuItem {
    kind: 'portal' | 'link';
    id: string;
    label: string;
    /** Its folder in the scene menu ("Building/Floor 1"), or ''. */
    path: string;
}

/**
 * The scene menu's items, with the page menu's rules: portals shown in the menu (a physical
 * portal whose target has a menu-only entry is left out), then links, in `menuOrder` when any item
 * has one (portals ahead of links otherwise, in array order).
 */
export function sceneMenuItems(portals: readonly PortalData[], links: readonly SceneMenuLink[]): VrSceneMenuItem[] {
    const menuOnlyTargets = new Set(portals.filter((p) => p.menuOnly && p.targetSceneId).map((p) => p.targetSceneId));
    const collected: Array<{ item: VrSceneMenuItem; order: number; seq: number }> = [];
    let seq = 0;
    for (const p of portals) {
        if (p.isCurrentScene) continue;
        if (!p.menuOnly && p.showInMenu === false) continue;
        if (!p.menuOnly && p.targetSceneId && menuOnlyTargets.has(p.targetSceneId)) continue;
        const ordered = typeof p.menuOrder === 'number' && Number.isFinite(p.menuOrder);
        const label = (p.title || p.targetSceneName || '').trim() || 'Untitled';
        collected.push({ item: { kind: 'portal', id: p.id, label, path: (p.menuPath ?? '').trim() }, order: ordered ? p.menuOrder! : seq, seq });
        seq++;
    }
    for (const l of links) {
        const ordered = typeof l.menuOrder === 'number' && Number.isFinite(l.menuOrder);
        collected.push({ item: { kind: 'link', id: l.id, label: l.label?.trim() || l.url, path: (l.menuPath ?? '').trim() }, order: ordered ? l.menuOrder! : seq, seq });
        seq++;
    }
    collected.sort((a, b) => a.order - b.order || a.seq - b.seq);
    return collected.map((c) => c.item);
}

/** The Scenes panel's rows: items without a folder first, then each folder's under its name. */
export function sceneRows(items: readonly VrSceneMenuItem[]): VrListRow[] {
    const rows: VrListRow[] = [];
    const folders: string[] = [];
    for (const item of items) if (item.path && !folders.includes(item.path)) folders.push(item.path);
    const row = (item: VrSceneMenuItem): VrListRow => ({ id: `${item.kind}:${item.id}`, label: item.label, detail: item.kind === 'link' ? '↗' : undefined });
    for (const item of items) if (!item.path) rows.push(row(item));
    for (const folder of folders) {
        rows.push({ id: `heading:${folder}`, label: folder.split('/').map((s) => s.trim()).filter(Boolean).join(' › '), heading: true });
        for (const item of items) if (item.path === folder) rows.push(row(item));
    }
    return rows;
}

// ── Links saved for after VR ──────────────────────────────────────────────────────────────────

export interface VrSavedLink {
    url: string;
    label: string;
}

/** Saving a link twice keeps one (the first label). */
export function addSavedLink(links: readonly VrSavedLink[], link: VrSavedLink): VrSavedLink[] {
    return links.some((l) => l.url === link.url) ? [...links] : [...links, link];
}

export function savedLinkRows(links: readonly VrSavedLink[]): VrListRow[] {
    return links.map((l, i) => ({ id: `saved:${i}`, label: l.label, sub: l.url }));
}

// ── Popups ────────────────────────────────────────────────────────────────────────────────────

/** A popup's fields the VR popup panel reads (hotspots, models, overlays and scene-menu media). */
export interface VrPopupSource {
    title?: string;
    information?: string;
    text?: string;
    photoUrl?: string;
    imageUrl?: string;
    popupVideoUrl?: string;
    videoUrl?: string;
    iframeUrl?: string;
    pdfUrl?: string;
    externalLinkUrl?: string;
    linkUrl?: string;
    linkText?: string;
    modelUrl?: string;
    contentBlocks?: ReadonlyArray<{ kind: string; text?: string; urls?: readonly string[]; url?: string }>;
    backgroundColor?: string;
    textColor?: string;
}

/** What the popup panel shows: text, a gallery, a video, a PDF, a link to save, a note. */
export interface VrPopupContent {
    title: string;
    text: string;
    images: string[];
    video: string | null;
    pdf: string | null;
    link: VrSavedLink | null;
    /** Content that can't show in VR (a 3D model). */
    model: boolean;
}

const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

export function vrPopupContent(source: VrPopupSource): VrPopupContent {
    const blocks = Array.isArray(source.contentBlocks) ? source.contentBlocks : [];
    const text = [source.information, source.text, ...blocks.filter((b) => b.kind === 'text').map((b) => b.text)]
        .filter(nonEmpty)
        .map((t) => t.trim())
        .join('\n\n');
    const images: string[] = [];
    const addImage = (url: unknown) => { if (nonEmpty(url) && !images.includes(url)) images.push(url); };
    addImage(source.photoUrl);
    addImage(source.imageUrl);
    for (const b of blocks) if (b.kind === 'image' && Array.isArray(b.urls)) b.urls.forEach(addImage);
    const blockUrl = (kind: string) => blocks.find((b) => b.kind === kind && nonEmpty(b.url))?.url ?? null;
    const video = nonEmpty(source.popupVideoUrl) ? source.popupVideoUrl : nonEmpty(source.videoUrl) ? source.videoUrl : blockUrl('video');
    const pdf = nonEmpty(source.pdfUrl) ? source.pdfUrl : blockUrl('pdf');
    const title = nonEmpty(source.title) ? source.title.trim() : '';
    let link: VrSavedLink | null = null;
    const linkBlock = blocks.find((b) => (b.kind === 'link' || b.kind === 'iframe') && nonEmpty(b.url));
    const linkUrl = [source.externalLinkUrl, source.linkUrl, source.iframeUrl, linkBlock?.url].find(nonEmpty);
    if (linkUrl) link = { url: linkUrl, label: (nonEmpty(source.linkText) ? source.linkText : linkBlock?.text?.trim()) || title || linkUrl };
    return { title, text, images, video: video ?? null, pdf: pdf ?? null, link, model: nonEmpty(source.modelUrl) || blocks.some((b) => b.kind === 'model') };
}
